import {
  Component,
  OnInit,
  EventEmitter,
  Output,
  Input,
  ViewEncapsulation,
  Inject,
  LOCALE_ID,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import * as moment from 'moment';
import { DateRangePickerOptionsInterface } from './@models/date-range-picker.options';
import { DateRangePickerInputEvent } from './@models/date-range-picker-range.model';
import { MwDateRangePickerService } from './@service/date-range-picker.service';
import { SideEnum } from './@models/date-range-picker-calendar-side.enum';
import { DateRangePickerLocaleConfig } from './@models/date-range-picker.locale-config';

@Component({
  selector: 'mw-date-range-picker-box',
  styleUrls: ['./date-range-picker.component.scss'],
  templateUrl: './date-range-picker.component.html',
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: {
    '(click)': 'handleInternalClick($event)',
  },
  encapsulation: ViewEncapsulation.None,
})
export class MwDateRangePickerComponent implements OnInit {
  calendarVariables: { left: any; right: any } = { left: {}, right: {} };
  dateRangePicker: { start: UntypedFormControl; end: UntypedFormControl } = {
    start: new UntypedFormControl(),
    end: new UntypedFormControl(),
  };
  startDate = null;
  endDate = null;
  endHoverDate: moment.Moment;
  get formattedDate(): Date {
    return this.endHoverDate.toDate();
  }
  durationInDays = 0;
  hoverDurationInDays = 0;
  // used in template for compile time support of enum values.
  sideEnum = SideEnum;

  get options(): DateRangePickerOptionsInterface {
    return this.localOptions;
  }

  @Input() set options(opts: DateRangePickerOptionsInterface) {
    this.localOptions = opts;
    this.localRangesArray = this.localOptions.ranges || [];
  }

  @Input() set value(range: DateRangePickerInputEvent) {
    if (range && range.start && range.end) {
      this.setStartDate(moment(range.start).clone());
      this.setEndDate(moment(range.end).clone());
      this.updateCalendars();
    }
  }

  get locale(): DateRangePickerLocaleConfig {
    return this.localOptions.locale;
  }

  get ranges(): DateRangePickerInputEvent[] {
    return this.localRangesArray;
  }

  chosenRange: string;

  inline = true;
  leftCalendar: any = {};
  rightCalendar: any = {};
  daysOfWeek = [];

  @Output() rangeChange = new EventEmitter<DateRangePickerInputEvent>();
  @Output() closed = new EventEmitter<void>();

  private localRangesArray: Array<DateRangePickerInputEvent> = [];
  private localOptions: DateRangePickerOptionsInterface;
  private localOld: {
    start?: moment.Moment;
    end?: moment.Moment;
    label?: string;
  } = {};

  constructor(
    @Inject(LOCALE_ID) public localeId: string,
    private service: MwDateRangePickerService
  ) {}

  ngOnInit(): void {
    this.updateMonthsInView();
    this.daysOfWeek = [...this.locale.daysOfWeek];

    if (this.locale.firstDay !== 0) {
      let iterator = this.locale.firstDay;
      while (iterator > 0) {
        this.daysOfWeek.push(this.daysOfWeek.shift());
        iterator--;
      }
    }
    if (this.inline) {
      this.localOld.start = this.startDate && this.startDate.clone();
      this.localOld.end = this.endDate && this.endDate.clone();
    }
    if (!this.locale.format) {
      this.locale.format = moment.localeData().longDateFormat('L');
    }
    this.renderCalendar(SideEnum.left);
    this.renderCalendar(SideEnum.right);
    this.renderRanges();
  }

  onCalendarsMouseLeave(): void {
    this.endHoverDate = null;
    this.updateCalendars();
  }

  renderRanges(): void {
    this.localRangesArray = this.service.buildRanges();

    if (this.startDate) {
      this.startDate = this.startDate.startOf('day');
    }

    if (this.endDate) {
      this.endDate = this.endDate.endOf('day');
    }
  }

  renderCalendar(side: SideEnum): void {
    this.service.buildCalendar(
      this.startDate,
      this.endDate,
      this.endHoverDate,
      this.calendarVariables,
      side,
      this.leftCalendar,
      this.rightCalendar
    );
  }
  setStartDate(startDate): void {
    this.endHoverDate = null;
    this.startDate = this.service.getNormalizedStart(startDate);

    this.updateMonthsInView();
  }

  setEndDate(endDate): void {
    this.endHoverDate = null;
    this.endDate = this.service.getNormalizedEnd(endDate, this.startDate);
    this.updateMonthsInView();
  }

  updateView(): void {
    this.updateMonthsInView();
    this.updateCalendars();
  }

  updateMonthsInView(): void {
    this.service.updateMonthsInView(
      this.startDate,
      this.endDate,
      this.rightCalendar,
      this.leftCalendar
    );
  }

  updateCalendars(): void {
    this.renderCalendar(SideEnum.left);
    this.renderCalendar(SideEnum.right);
    this.updateDuration();

    if (this.endDate === null) {
      return;
    }
    this.calculateChosenLabel();
  }

  updateDuration(): void {
    this.durationInDays = this.service.getDuration(
      this.startDate,
      this.endDate
    );

    this.hoverDurationInDays = this.service.getDuration(
      this.startDate,
      this.endHoverDate
    );
  }

  /**
   * this should calculate the label
   */
  calculateChosenLabel(): void {
    this.chosenRange = this.service.getChoosenRange(
      this.startDate,
      this.endDate
    );
  }

  clickApply(e?): void {
    if (this.startDate && !this.endDate) {
      this.endDate = this.startDate.clone();
    }
    this.calculateChosenLabel();
    this.rangeChange.emit({
      label: this.chosenRange,
      start: this.startDate.toDate(),
      end: this.endDate.toDate(),
    });
    this.hide();
  }

  clickCancel(e): void {
    this.startDate = this.localOld.start;
    this.endDate = this.localOld.end;
    if (this.inline) {
      this.updateView();
    }
    this.hide();
  }
  /**
   * called when month is changed
   * @param monthEvent get value in event.target.value
   * @param side left or right
   */
  monthChanged(monthEvent: any, side: SideEnum): void {
    const year = this.calendarVariables[side].dropdowns.currentYear;
    const month = parseInt(monthEvent.target.value, 10);
    this.monthOrYearChanged(month, year, side);
  }
  /**
   * called when year is changed
   * @param yearEvent get value in event.target.value
   * @param side left or right
   */
  yearChanged(yearEvent: any, side: SideEnum): void {
    const month = this.calendarVariables[side].dropdowns.currentMonth;
    const year = parseInt(yearEvent.target.value, 10);
    this.monthOrYearChanged(month, year, side);
  }
  /**
   *  call when month or year changed
   * @param month month number 0 -11
   * @param year year eg: 1995
   * @param side left or right
   */
  monthOrYearChanged(month: number, year: number, side: SideEnum): void {
    this.service.monthOrYearChanged(
      this.startDate,
      this.leftCalendar,
      this.rightCalendar,
      this.calendarVariables,
      month,
      year,
      side
    );
    this.updateCalendars();
  }

  /**
   * Click on previous month
   * @param side left or right calendar
   */
  clickPrev(side: SideEnum): void {
    if (side === SideEnum.left) {
      this.leftCalendar.month.subtract(1, 'month');
      if (this.localOptions.linkedCalendars) {
        this.rightCalendar.month.subtract(1, 'month');
      }
    } else {
      this.rightCalendar.month.subtract(1, 'month');
    }
    this.updateCalendars();
  }
  /**
   * Click on next month
   * @param side left or right calendar
   */
  clickNext(side: SideEnum): void {
    if (side === SideEnum.left) {
      this.leftCalendar.month.add(1, 'month');
    } else {
      this.rightCalendar.month.add(1, 'month');
      if (this.localOptions.linkedCalendars) {
        this.leftCalendar.month.add(1, 'month');
      }
    }
    this.updateCalendars();
  }
  /**
   * When selecting a date
   * @param e event: get value by e.target.value
   * @param side left or right
   * @param row row position of the current date clicked
   * @param col col position of the current date clicked
   */
  clickDate(e, side: SideEnum, row: number, col: number): void {
    if (e.target.tagName === 'TD') {
      if (!e.target.classList.contains('available')) {
        return;
      }
    } else if (e.target.tagName === 'SPAN') {
      if (!e.target.parentElement.classList.contains('available')) {
        return;
      }
    }
    if (this.localRangesArray.length) {
      this.chosenRange = this.locale.customRangeLabel;
    }

    const date =
      side === SideEnum.left
        ? this.leftCalendar.calendar[row][col]
        : this.rightCalendar.calendar[row][col];

    if (!date) {
      return;
    }

    if (
      !this.startDate ||
      this.endDate ||
      date.isBefore(this.startDate, 'day')
    ) {
      // picking start
      this.endDate = null;
      this.setStartDate(date.clone());
    } else if (!this.endDate && date.isBefore(this.startDate)) {
      // special case: clicking the same date for start/end,
      // but the time of the end date is before the start date
      this.setEndDate(this.startDate.clone());
    } else {
      // picking end
      this.setEndDate(date.clone());
      if (this.localOptions.autoApply) {
        this.calculateChosenLabel();
        this.clickApply();
      }
    }

    this.updateView();

    // This is to cancel the blur event handler if the mouse was in one of the inputs
    e.stopPropagation();
  }

  hoverDate(e, side: SideEnum, row: number, col: number): void {
    if (!this.startDate) {
      return;
    } else if (e.target.tagName === 'TD') {
      if (!e.target.classList.contains('available')) {
        return;
      }
    } else if (e.target.tagName === 'SPAN') {
      if (!e.target.parentElement.classList.contains('available')) {
        return;
      }
    }

    const date =
      side === SideEnum.left
        ? this.leftCalendar.calendar[row][col]
        : this.rightCalendar.calendar[row][col];

    if (
      !date ||
      date.isBefore(this.startDate, 'day') ||
      date.isAfter(this.localOptions.maxDate) ||
      (this.options.dateLimit &&
        this.startDate.clone().add(this.options.dateLimit).isBefore(date))
    ) {
      return;
    } else {
      if (typeof date === 'string') {
        this.endHoverDate = moment(date, this.locale.format);
      }

      if (typeof date === 'object') {
        this.endHoverDate = moment(date);
      }
    }

    this.updateCalendars();
  }

  clickRange(e, range): void {
    this.chosenRange = range.label;
    if (this.chosenRange === this.locale.customRangeLabel) {
      // nothing
    } else {
      this.startDate = moment(range.start).clone();
      this.endDate = moment(range.end).clone();

      this.calculateChosenLabel();

      this.startDate.startOf('day');
      this.endDate.endOf('day');

      if (this.localOptions.autoFocusMonthInView) {
        this.updateMonthsInView();
      }
      if (this.localOptions.autoApply) {
        this.clickApply();
      } else {
        this.updateCalendars();
      }
    }
  }

  hide(e?): void {
    this.closed.emit();
  }

  /**
   * handle click on all element in the component, usefull for outside of click
   * @param e event
   */
  handleInternalClick(e): void {
    e.stopPropagation();
  }

  updateLocale(locale): void {
    for (const key in locale) {
      if (locale.hasOwnProperty(key)) {
        this.locale[key] = locale[key];
      }
    }
  }
  /**
   *  clear the date-range picker
   */
  clear(emmitChange: boolean = true): void {
    this.startDate = moment().startOf('day');
    this.endDate = moment().endOf('day');
    this.endHoverDate = null;
    if (emmitChange) {
      this.rangeChange.emit({
        label: null,
        start: null,
        end: null,
      });
    }
    this.hide();
  }

  /**
   * Find out if the selected range should be disabled if it doesn't
   * fit into minDate and maxDate limitations.
   */
  isDisabledRange(range): boolean {
    return this.service.isDisabledRange(range);
  }

  calendarTdHasClass(className, side, row, col): boolean {
    return (
      this.calendarVariables[side].classes[row][col].indexOf(className) !== -1
    );
  }

  isRangeSelected(range: DateRangePickerInputEvent): boolean {
    return this.chosenRange && this.chosenRange === range.label;
  }

  showWeekNumbers(): boolean {
    return this.options.showWeekNumbers || this.options.showISOWeekNumbers;
  }

  getWeek(side: SideEnum, row: number): string {
    const calendarRow = this.calendarVariables[side].calendar[row];
    const date = calendarRow[0] || calendarRow[6];

    if (!date) {
      return '';
    }

    return this.options.showWeekNumbers ? date.week() : date.isoWeek();
  }
}
